// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LazyImage, { LazyImageProps } from "@bg/MobiusOverrides/LazyImage";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import { ButtonProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import { injectCss } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

// eslint-disable-next-line
import SwiperCore, { A11y, Navigation, Pagination, Scrollbar } from "swiper";
// eslint-disable-next-line
import { Swiper, SwiperSlide } from "swiper/react";
SwiperCore.use([Navigation, Pagination, Scrollbar, A11y]);

interface OwnProps {
    scrollingBehavior?: string;
    numberOfVisibleThumbnails?: number;
    selectedIndex?: number;
    onSelectImage?: (index: number) => void;
    extendedStyles?: ProductImageCarouselStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

type Props = OwnProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductImageCarouselStyles {
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    /** @deprecated Use CarouselSlideContainer object instead. */
    carouselSlidesContainer?: InjectableCss;
    /** @deprecated Use CarouselSlide object instead. */
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    image?: LazyImageProps;
    selectedImage?: LazyImageProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonProps;
    verticalPrevArrowGridItem?: GridItemProps;
    verticalCarouselGridItem?: GridItemProps;
    verticalNextArrowGridItem?: GridItemProps;
}

export const productImageCarouselStyles: ProductImageCarouselStyles = {
    carouselWrapper: {
        css: css`
            @media print {
                display: none;
            }
            .swiper-container {
                @media (max-width: 760px) {
                    height: 88px !important;
                    max-width: 300px;
                }
            }
            width: 88px;
            position: relative;
            max-height: 412px;
            .swiper-carousel {
                height: 412px;
                overflow: hidden;
                width: 88px;
                padding-top: 5px;

                @media (max-width: 760px) {
                    width: 100%;
                    padding-top: 0;
                }
            }

            .swiper-button-prev-pdp-small,
            .swiper-button-next-pdp-small {
                display: none;
            }

            @media (max-width: 760px) {
                height: 88px;
                order: 2;
                width: 100%;
                padding-top: 0;
                .swiper-button-prev-pdp-small,
                .swiper-button-next-pdp-small {
                    color: ${getColor("text.main")};
                    position: absolute;
                    display: block;
                    top: 50%;
                    transform: translateY(-50%);
                    z-index: 2;
                    &.hide-navigation {
                        display: none;
                    }
                }

                .swiper-button-prev-pdp-small {
                    left: 10px;
                }

                .swiper-button-next-pdp-small {
                    right: 10px;
                }
            }

            .swiper-button-disabled,
            .swiper-button-prev-pdp[aria-disabled="true"],
            .swiper-button-next-pdp[aria-disabled="true"],
            .swiper-button-prev-pdp-small[aria-disabled="true"],
            .swiper-button-next-pdp-small[aria-disabled="true"] {
                display: none;
            }

            .swiper-button-prev-pdp,
            .swiper-button-next-pdp {
                color: ${getColor("text.main")};
                position: absolute;
                left: 50%;
                transform: translate(-50%);
                z-index: 2;

                @media (max-width: 760px) {
                    display: none;
                }

                &.hide-navigation {
                    display: none;
                }
            }

            .swiper-button-prev-pdp {
                top: -25px;
            }
            .swiper-button-next-pdp {
                bottom: -25px;
            }
            .swiper-slide {
                height: 88px !important;
                @media (max-width: 760px) {
                    display: flex;
                    justify-content: center;
                }
            }
        `,
    },
    image: {
        css: css`
            &&:hover {
                cursor: pointer;
            }
            border: 1px solid ${getColor("common.ui3")};
            padding: 10px;
            img {
                max-width: 66px;
            }
        `,
    },
    verticalPrevArrowGridItem: {
        width: 12,
        align: "middle",
        css: css`
            justify-content: center;
        `,
    },
    selectedImage: {
        css: css`
            border: 1px solid ${getColor("primary.main")};
            padding: 10px;
        `,
    },
    verticalCarouselGridItem: { width: 12 },
    verticalNextArrowGridItem: {
        width: 12,
        align: "middle",
        css: css`
            justify-content: center;
        `,
    },
};

// Xng-Change: Comment out below code as we use swiper carousel

// export const CarouselSlidesContainer = styled.div<
//     InjectableCss & { isVertical: boolean; numberOfVisibleThumbnails: number }
// >`
//     display: flex;
//     flex-direction: ${props => (props.isVertical ? "column" : "row")};
//     align-items: ${props => (props.isVertical ? "center" : "initial")};
//     ${props => (props.isVertical ? `height: ${props.numberOfVisibleThumbnails * 74}px;` : "")}
//     ${injectCss};
// `;

// export const CarouselSlide = styled.div<InjectableCss & { numberOfVisibleThumbnails: number }>`
//     flex: 0 0 ${props => 100 / props.numberOfVisibleThumbnails}%;
//     padding: 0;
//     position: relative;
//     ${injectCss};
// `;

const ProductImageCarousel = ({
    productSettings,
    productContext,
    /**
     * Xng-Change
     * Comment out:
     *  scrollingBehavior as it's not supported in SwiperSlide
     *  numberOfVisibleThumbnails as design is set for 4 items
     *  */
    // scrollingBehavior,
    // numberOfVisibleThumbnails = 6,
    selectedIndex,
    onSelectImage,
    extendedStyles,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productImageCarouselStyles, extendedStyles));
    const { product } = productContext;
    const [showSpinner, setShowSpinner] = React.useState(true);

    React.useEffect(() => {
        // eslint-disable-next-line global-require
        const getStyles = async () => {
            // eslint-disable-next-line global-require
            const result = await require("swiper/swiper-bundle.min.css");
        };

        getStyles().then(result => {
            setShowSpinner(false);
        });
    }, []);

    const [selectedImageIndex, setSelectedImageIndex] = useState(selectedIndex);
    useEffect(() => {
        setSelectedImageIndex(selectedIndex);
    }, [selectedIndex]);

    const imageClickHandler = (index: number) => {
        setSelectedImageIndex(index);
        onSelectImage?.(index);
    };

    /**
     * Xng-Change
     * Comment out below code as it's not used
     */
    // const isVertical = scrollingBehavior === "vertical";
    // const [emblaRef, embla] = useEmblaCarousel({
    //     axis: isVertical ? "y" : "x",
    // });

    const getProductImageThumbPath = (productImage: ImageModel) => {
        if (productImage.imageType === "Static") {
            return productImage.smallImagePath || productImage.mediumImagePath;
        }
        if (productImage.imageType === "360" && productSettings.imageProvider === "SIRV") {
            return productImage.mediumImagePath + (productImage.mediumImagePath.includes("?") ? "&thumb" : "?thumb");
        }
        return "";
    };

    if (!product || !product.images || product.images.length === 0) {
        return <StyledWrapper {...styles.carouselWrapper}></StyledWrapper>;
    }

    // loop as false to fix the index on mobile, the image selected will be different than the one showing -- keeping it false to fix the indexes
    return (
        <StyledWrapper {...styles.carouselWrapper}>
            <Swiper
                navigation={{
                    nextEl: ".swiper-button-next-pdp",
                    prevEl: ".swiper-button-prev-pdp",
                }}
                direction="vertical"
                className="swiper-carousel"
                spaceBetween={15}
                loop={false}
                slidesPerView={4}
                breakpoints={{
                    0: {
                        slidesPerView: 1,
                        direction: "horizontal",
                        navigation: {
                            prevEl: ".swiper-button-prev-pdp-small",
                            nextEl: ".swiper-button-next-pdp-small",
                        },
                    },
                    761: {
                        slidesPerView: 4,
                    },
                }}
            >
                {product.images.map((productImage, index) => (
                    <SwiperSlide key={productImage.id} className="swiper-slide">
                        <StyledWrapper {...styles.carouselSlidesContainer}>
                            <StyledWrapper {...styles.carouselSlideInner}>
                                <LazyImage
                                    {...(index === selectedImageIndex ? styles.selectedImage : styles.image)}
                                    width="66px"
                                    height="66px"
                                    cloudinaryWidth="66"
                                    cloudinary={true}
                                    src={getProductImageThumbPath(productImage)}
                                    altText={productImage.imageAltText}
                                    onClick={() => imageClickHandler(index)}
                                />
                            </StyledWrapper>
                        </StyledWrapper>
                    </SwiperSlide>
                ))}
            </Swiper>
            <div className={`swiper-button-prev-pdp ${product.images.length < 4 ? "hide-navigation" : ""}`}>
                <Icon src="KeyboardArrowUp" size={18} />
            </div>
            <div className={`swiper-button-next-pdp ${product.images.length < 4 ? "hide-navigation" : ""}`}>
                <Icon src="KeyboardArrowDown" size={18} />
            </div>
            <div className={`swiper-button-prev-pdp-small ${product.images.length < 2 ? "hide-navigation" : ""}`}>
                <Icon src="KeyboardArrowLeft" size={18} />
            </div>
            <div className={`swiper-button-next-pdp-small ${product.images.length < 2 ? "hide-navigation" : ""}`}>
                <Icon src="KeyboardArrowRight" size={18} />
            </div>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductImageCarousel));
